import { useState, useEffect, useCallback } from 'react';
import { UserWithRole, Role, Referent } from '../../../../shared/services/userService';
import userService from '../../../../shared/services/userService';

interface UseUserManagementProps {
  searchTerm: string;
  selectedRole: number | '';
  page: number;
}

export const useUserManagement = ({ searchTerm, selectedRole, page }: UseUserManagementProps) => {
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [usersReferents, setUsersReferents] = useState<Map<number, Referent[]>>(new Map());

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [usersResponse, rolesResponse] = await Promise.all([
        userService.getUsers({
          search: searchTerm || undefined,
          role: selectedRole || undefined,
          page,
          limit: 20,
        }),
        userService.getRoles(),
      ]);

      if (usersResponse.success) {
        setUsers(usersResponse.data);
        if (usersResponse.pagination) {
          setTotalPages(usersResponse.pagination.totalPages);
        }

        // Charger les référents pour tous les utilisateurs de l'équipe (roles_id <= 4)
        const teamUsers = usersResponse.data.filter(user => user.roles_id >= 1 && user.roles_id <= 4);
        const referentsMap = new Map<number, Referent[]>();
        
        await Promise.all(
          teamUsers.map(async (user) => {
            try {
              const referentsResponse = await userService.getUserReferents(user.id);
              if (referentsResponse.success && referentsResponse.data.length > 0) {
                referentsMap.set(user.id, referentsResponse.data);
              }
            } catch (err) {
              // Ignorer les erreurs pour les utilisateurs sans référents
            }
          })
        );
        
        setUsersReferents(referentsMap);
      }

      if (rolesResponse.success) {
        setRoles(rolesResponse.data);
      }
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, selectedRole]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    users,
    roles,
    loading,
    error,
    totalPages,
    usersReferents,
    setError,
    reload: loadData,
  };
};

